// pages/TravelExpense/index.js
//
// Legacy menu: "Travel Expense Approval"
// Backend:
//   GET /api/TravelExpense/my-requests
//   GET /api/TravelExpense/awaiting-approval     (manager-only)
//
// Tabs:
//   "My Trips"             — caller's own requests (travel.mine)
//   "Awaiting My Approval" — trips needing the caller's decision (travelExpense.approval)
//   "All Requests"         — every trip past Draft, read-only (legacy MPC
//                            Request Viewer, "MPC EPO Viewer" / IT Admin)
// Go-live parity (2026-09-24): #31 — ?tab=my|awaiting|all deep links; the
// routes /travel-expense/approvals and /travel-expense/all-requests open
// their tab directly (initialTab).

import React, { useEffect, useMemo, useState } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Card, CardBody, Alert, Button } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    DashboardPageHeader, EmptyState, StandardTableToolbar, ResponsiveTabs, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";
import { toCamel } from "./travelCase";
import { TravelStatusBadge, statusText } from "./travelStatus";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

const TAB_URLS = {
    my: "TravelExpense/my-requests",
    awaiting: "TravelExpense/awaiting-approval",
    all: "TravelExpense/all-requests",
};

const TravelExpenseIndex = ({ initialTab }) => {
    const navigate = useNavigate();
    const auth = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    // Go-live parity (2026-09-24): #15/#25 — tabs and the New button follow
    // the module keys (IT Admin passes), not the raw manager flag.
    const canMine = !!(auth.isItAdmin || auth.canAccessModule("travel.mine"));
    const canNew = !!(auth.isItAdmin || auth.canAccessModule("travel.new"));
    const canApprove = !!(auth.isItAdmin || auth.canAccessModule("travelExpense.approval"));
    const canSeeAll = !!(auth.isMPC || auth.isItAdmin);   // #24 legacy MPC Request Viewer

    const [awaitingCount, setAwaitingCount] = useState(null);
    const TABS = useMemo(() => [
        ...(canMine ? [{ key: "my", label: "My Trips" }] : []),
        ...(canApprove ? [{ key: "awaiting", label: "Awaiting My Approval", count: awaitingCount ?? undefined }] : []),
        ...(canSeeAll ? [{ key: "all", label: "All Requests" }] : []),
    ], [canMine, canApprove, canSeeAll, awaitingCount]);

    const wanted = searchParams.get("tab") || initialTab;
    const firstTab = TABS.length > 0 ? TABS[0].key : "my";
    const [activeTab, setActiveTabState] = useState(
        TABS.some((t) => t.key === wanted) ? wanted : firstTab);
    const setActiveTab = (key) => {
        setActiveTabState(key);
        const next = new URLSearchParams(searchParams);
        next.set("tab", key);
        setSearchParams(next);   // Owner 2026-10-12: Back returns to the previous tab
    };
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        const url = TAB_URLS[activeTab] || TAB_URLS.my;
        api.get(url)
            .then(({ data }) => {
                if (!alive) return;
                // Go-live parity (2026-09-24): camelCase response (was data.Items only).
                const d = toCamel(data);
                const list = Array.isArray(d?.items) ? d.items : [];
                setItems(list);
                if (activeTab === "awaiting") setAwaitingCount(list.length);
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [activeTab]);

    // Go-live parity (2026-09-24): #31 — pending count on the approval tab
    // (legacy menu tree showed counts), fetched once when another tab is open.
    useEffect(() => {
        if (!canApprove || activeTab === "awaiting") return;
        let alive = true;
        api.get(TAB_URLS.awaiting)
            .then(({ data }) => { if (alive) setAwaitingCount((toCamel(data)?.items ?? []).length); })
            .catch(() => { /* count is optional */ });
        return () => { alive = false; };
    }, [canApprove]);  // eslint-disable-line

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) =>
            String(r.nameOfTrip ?? "").toLowerCase().includes(q)
            || String(r.primaryObjective ?? "").toLowerCase().includes(q)
            || String(r.requesterName ?? "").toLowerCase().includes(q)
        );
    }, [items, search]);

    // Round 36 — EPO-style per-column filters + click-to-sort + CSV.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => [
        { id: "id",        label: "ID",   type: "text", get: (r) => String(r.id ?? "") },
        { id: "nameOfTrip",label: "Trip", type: "text", get: (r) => r.nameOfTrip || "" },
        { id: "primaryObjective", label: "Primary Objective", type: "text", get: (r) => r.primaryObjective || "" },
        ...(activeTab !== "my" ? [{ id: "requesterName", label: "Requester", type: "text", get: (r) => r.requesterName || "" }] : []),
        // Go-live parity (2026-09-24): #31 — legacy grid "Date Prepared" (SubmitDate).
        { id: "submitDate",     label: "Date Prepared", type: "date", get: (r) => r.submitDate || "" },
        { id: "leaveHomeDate",  label: "Leave",  type: "date", get: (r) => r.leaveHomeDate || "" },
        { id: "returnHomeDate", label: "Return", type: "date", get: (r) => r.returnHomeDate || "" },
        { id: "status", label: "Status", type: "text", get: (r) => statusText(r.status, r.statusLabel) },
        { id: "total",  label: "Estimated Total", type: "text", get: (r) => String(r.total ?? "") },
        { id: "act", label: "", type: "none" },
    ], [activeTab]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="Travel Expense Approval"
                    breadcrumb={[{ label: "Travel Expense Approval" }]}
                    subtitle={activeTab === "my"
                        ? `Your travel requests (${items.length})`
                        : activeTab === "all"
                            ? `All travel requests past Draft (${items.length})`
                            : `Trips awaiting your approval (${items.length})`}
                    breadcrumbItems={[{ label: "Travel Expense" }]}
                    actions={canNew ? (   // Go-live parity (2026-09-24): #25 — only with travel.new
                        <Button color="primary" onClick={() => navigate("/travel-expense/new")}>
                            + New Request
                        </Button>
                    ) : undefined}
                />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}
                <ResponsiveTabs tabs={TABS} activeKey={activeTab} onChange={setActiveTab} />  {/* R40: items → tabs */}
                <Card className="mt-3">
                    <CardBody>
                        <StandardTableToolbar right={
                                {/* Round 36 — search replaced by the per-column filter row */}
                        } />
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={6} columns={10} /></tbody>
                                </table>
                            </div>
                        ) : view.length === 0 ? (
                            <EmptyState
                                title={activeTab === "my" ? "No travel requests yet" : activeTab === "all" ? "No travel requests" : "Nothing waiting on you"}
                                description={activeTab === "my"
                                    ? "Your submitted trips will appear here."
                                    : activeTab === "all"
                                        ? "Submitted, approved and denied trips will appear here."
                                        : "There are no travel requests awaiting your approval."}
                            />
                        ) : (
                            <div className="table-responsive">
                                {/* Round 36 — CSV of the filtered + sorted list */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm"
                                        onClick={() => exportRowsToCsv("travel-requests.csv", tblColumns.filter((c) => c.type !== "none"), view)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                    </button>
                                </div>
                                <table className="table table-striped table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {tblColumns.map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                        </tr>
                                        <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {view.map((r) => (
                                            <tr key={r.id}>
                                                <td>{r.id}</td>
                                                <td style={{ maxWidth: 260 }} className="text-truncate" title={r.nameOfTrip ?? ""}>{r.nameOfTrip ?? "—"}</td>
                                                <td style={{ maxWidth: 260 }} className="text-truncate" title={r.primaryObjective ?? ""}>{r.primaryObjective ?? "—"}</td>
                                                {activeTab !== "my" && <td>{r.requesterName ?? r.empid}</td>}
                                                <td>{fmtDate(r.submitDate)}</td>
                                                <td>{fmtDate(r.leaveHomeDate)}</td>
                                                <td>{fmtDate(r.returnHomeDate)}</td>
                                                <td><TravelStatusBadge status={r.status} label={r.statusLabel} style={{ fontSize: 11 }} /></td>
                                                <td className="text-end">{fmtMoney(r.total)}</td>
                                                <td>
                                                    <button type="button" className="btn btn-link btn-sm p-0"
                                                        onClick={() => navigate(`/travel-expense/${r.id}`)}>
                                                        {activeTab === "awaiting" ? "Review" : "View"}
                                                    </button>
                                                    {/* Go-live parity (2026-09-24): the owner reopens a saved
                                                        Draft (Status 1) — or, #9, a Denied (4) request to
                                                        correct and resubmit — in the edit form (legacy My
                                                        Requests). */}
                                                    {activeTab === "my" && (r.status === 1 || r.status === 4)
                                                        && String(r.empid ?? "").trim() === String(auth.empId ?? "").trim() && (
                                                        <button type="button" className="btn btn-link btn-sm p-0 ms-2"
                                                            onClick={() => navigate(`/travel-expense/${r.id}/edit`)}>
                                                            {r.status === 4 ? "Edit / Resubmit" : "Edit / Submit"}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TravelExpenseIndex;
